import { c as _c } from "react/compiler-runtime";
import CopilotIcon from '@precis-digital/kurama/icons/CopilotIcon';
import DataInsightsIcon from '@precis-digital/kurama/icons/DataInsightsIcon';
import PrecisAppsIcon from '@precis-digital/kurama/icons/PrecisAppsIcon';
import RecommendationsIcon from '@precis-digital/kurama/icons/RecommendationsIcon';
import SettingsIcon from '@precis-digital/kurama/icons/SettingsIcon';
import ShopIcon from '@precis-digital/kurama/icons/ShopIcon';
import type { NavContentItem } from '@precis-digital/kurama/SideNavigation';
import { type NiceModalStore } from '@ebay/nice-modal-react';
import { type ReactElement, type ReactNode } from 'react';
import { type ClientFeatureType } from 'shared/api/clients';
import { RetainDefaultQueryLink as Link } from 'shared/components/Router';
import { POPUP_QUERY_PARAMETER_KEY } from 'shared/constants';
import { ALVIE_APPS_REGISTRY } from 'shared/constants/apps';
import { type OverScreenIds } from 'shared/overScreens/types';
import { OverScreen } from 'shared/overScreens/niceModalReact';
import { APP_ROUTES } from 'shared/routes';
import { type TFunction } from 'shared/translations';
export const NOTIFICATION_COUNT_MAX = 99;
const EMPTY_NOTIFICATION_BADGE_VALUE = '';
const HOME_PATH = '/';
const PRECIS_APPS_URL = 'https://www.precisapps.io';
const EXTERNAL_LINK_TARGET = '_blank';
const EXTERNAL_LINK_REL = 'noopener noreferrer';
const EMPTY_HREF = '';
interface LinkProps {
  children: ReactNode;
  href?: string;
}
const NavLink = props => {
  const $ = _c(3);
  const t0 = props.href ?? EMPTY_HREF;
  let t1;
  if ($[0] !== props.children || $[1] !== t0) {
    t1 = <Link href={t0}>{props.children}</Link>;
    $[0] = props.children;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
interface BuildNavContentItemsParams {
  t: TFunction;
  isSideNavItemLocked: (featureIds: ClientFeatureType[]) => boolean;
  shouldShowInternalFeatures: boolean;
  canViewCopilotFullscreen: boolean | undefined;
}
export const buildNavContentItems = ({
  t,
  isSideNavItemLocked,
  shouldShowInternalFeatures,
  canViewCopilotFullscreen
}: BuildNavContentItemsParams): NavContentItem[] => {
  const creativeNavItem: NavContentItem = {
    label: t('sideNavigation.creative'),
    IconComponent: ALVIE_APPS_REGISTRY['creative-reporting'].icon,
    subItems: [{
      label: t('sideNavigation.reporting'),
      Link: NavLink,
      path: ALVIE_APPS_REGISTRY['creative-reporting'].uri,
      locked: isSideNavItemLocked(['reportingSolutions'])
    }, {
      label: t('sideNavigation.assets'),
      Link: NavLink,
      path: ALVIE_APPS_REGISTRY['creative-assets'].uri,
      locked: isSideNavItemLocked(['creativeAssets'])
    }]
  };
  const copilotNavItem: NavContentItem = {
    label: t('sideNavigation.copilot'),
    IconComponent: <CopilotIcon />,
    subItems: [{
      label: t('sideNavigation.copilotNewChat'),
      Link: NavLink,
      path: APP_ROUTES.copilot.chatPage
    }, {
      label: t('sideNavigation.copilotSettings'),
      Link: NavLink,
      path: APP_ROUTES.copilot.settingsGeneralPage
    }]
  };
  const precisAppsNavItem: NavContentItem = {
    label: t('sideNavigation.precisApps'),
    IconComponent: <PrecisAppsIcon />,
    Link: (props: LinkProps) => <a href={PRECIS_APPS_URL} target={EXTERNAL_LINK_TARGET} rel={EXTERNAL_LINK_REL}>
        {props.children}
      </a>,
    path: PRECIS_APPS_URL
  };
  const copilotNavItems: NavContentItem[] = canViewCopilotFullscreen === true ? [copilotNavItem] : [];
  const internalFeatureItems: NavContentItem[] = shouldShowInternalFeatures ? [precisAppsNavItem] : [];
  return [{
    label: t('sideNavigation.home'),
    Link: NavLink,
    IconComponent: <ShopIcon />,
    path: HOME_PATH
  }, {
    label: t('sideNavigation.recommendations'),
    Link: NavLink,
    IconComponent: <RecommendationsIcon />,
    path: APP_ROUTES.recommendations.basePage
  }, {
    label: t('sideNavigation.setup'),
    IconComponent: <SettingsIcon />,
    subItems: [{
      label: t('sideNavigation.connectDataSource'),
      Link: NavLink,
      path: APP_ROUTES.dataSources.basePage
    }, {
      label: t('sideNavigation.customGroupings'),
      Link: NavLink,
      path: APP_ROUTES.customGroupings.basePage
    }]
  }, {
    label: t('sideNavigation.dataAndReporting'),
    IconComponent: <DataInsightsIcon />,
    subItems: [{
      label: t('sideNavigation.connectors'),
      Link: NavLink,
      path: ALVIE_APPS_REGISTRY['bifrost-extract-connectors'].uri,
      locked: isSideNavItemLocked(['connectors'])
    }, {
      label: t('sideNavigation.reportingSolutions'),
      Link: NavLink,
      path: ALVIE_APPS_REGISTRY['precis-reporting-solutions'].uri,
      locked: isSideNavItemLocked(['reportingSolutions'])
    }],
    locked: isSideNavItemLocked(['connectors', 'reportingSolutions'])
  }, {
    label: t('sideNavigation.enhancedSignals'),
    Link: NavLink,
    IconComponent: ALVIE_APPS_REGISTRY['enhanced-signals'].icon,
    path: ALVIE_APPS_REGISTRY['enhanced-signals'].uri,
    locked: isSideNavItemLocked(['enhancedSignals'])
  }, {
    label: t('sideNavigation.marketingEvaluation'),
    IconComponent: ALVIE_APPS_REGISTRY['attribution-models'].icon,
    subItems: [{
      label: t('sideNavigation.attributionModels'),
      Link: NavLink,
      path: ALVIE_APPS_REGISTRY['attribution-models'].uri,
      locked: isSideNavItemLocked(['attributionModels'])
    }, {
      label: t('sideNavigation.budgetOptimiser'),
      Link: NavLink,
      path: ALVIE_APPS_REGISTRY['budget-optimiser'].uri,
      locked: isSideNavItemLocked(['budgetOptimiser'])
    }],
    locked: isSideNavItemLocked(['attributionModels', 'budgetOptimiser'])
  }, creativeNavItem, ...copilotNavItems, ...internalFeatureItems];
};
export const formatNotificationBadgeValue = (count: number, t: TFunction): string => {
  if (count === 0) {
    return EMPTY_NOTIFICATION_BADGE_VALUE;
  }
  if (count > NOTIFICATION_COUNT_MAX) {
    return t('notifications.maxCount', {
      count: NOTIFICATION_COUNT_MAX
    });
  }
  return `${count}`;
};
export const clearOverscreensOnRouteChange = (destinationUrl: string, overScreenContext: NiceModalStore | null | undefined): void => {
  if (destinationUrl.includes(POPUP_QUERY_PARAMETER_KEY)) {
    return;
  }
  if (overScreenContext != null && typeof overScreenContext === 'object') {
    Object.keys(overScreenContext).forEach(key => {
      OverScreen.remove(key as OverScreenIds);
    });
  }
};